import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Send, Square, Search, Pencil, Scissors, Mail } from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';
import MailingComposerModal from '../mailing/MailingComposerModal';
import ScrollToBottomButton from '../chat/ScrollToBottomButton';

// Session J+1 — Extrait un sujet d'email depuis un Markdown (cf ProjectDetail).
function extractSubjectFromMarkdown(md) {
  if (!md || typeof md !== 'string') return '';
  const lines = md.split('\n').map(l => l.trim()).filter(Boolean);
  const heading = lines.find(l => /^#{1,3}\s+/.test(l));
  let raw = heading ? heading.replace(/^#{1,3}\s+/, '') : (lines[0] || '');
  raw = raw
    .replace(/^>\s+/, '').replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .trim();
  if (raw.length > 100) raw = raw.slice(0, 97).trimEnd() + '…';
  return raw;
}
import VoiceButton from '../voice/VoiceButton';
import VoiceWidget from '../voice/VoiceWidget';
import InputToolbar from '../toolbar/InputToolbar';
import TemplateManager from '../toolbar/TemplateManager';
import ChatSearch from '../chat/ChatSearch';
import CommModeBadge from '../comm/CommModeBadge';
import CommTargetSummary from '../comm/CommTargetSummary';
import CommFormatSelector from '../comm/CommFormatSelector';
import CommDocumentPreview from '../comm/CommDocumentPreview';
import MarkdownMessage from '../chat/MarkdownMessage';
import StreamStatus from '../chat/StreamStatus';
import ConversationLengthIndicator from '../chat/ConversationLengthIndicator';
import PasteAttachments from '../chat/PasteAttachments';
import PlusMenu from '../chat/PlusMenu';
import { useStreamChat } from '../../hooks/useStreamChat';
import { usePasteHandler } from '../../hooks/usePasteHandler';

const ConversationChat = ({ conversationId, cmActive, user, forcedModel, onForcedModelChange, conversationMode, onPreview, autoOpenVoice }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [templatesVersion, setTemplatesVersion] = useState(0);
  const [editTemplateData, setEditTemplateData] = useState(null);
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [searchHighlightId, setSearchHighlightId] = useState(null);
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [helpMode, setHelpMode] = useState(false);
  // Session J+1 — Composer mailing pré-rempli depuis un message IA
  const [mailingModal, setMailingModal] = useState(null);

  // Edit message user (chat général)
  const handleEditMessage = async (msgId) => {
    if (!editContent.trim()) return;
    try {
      const token = localStorage.getItem('oracle_token');
      await fetch(`/api/chat/conversations/${conversationId}/messages/${msgId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editContent }),
      });
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, content: editContent } : m));
      setEditingMsgId(null);
      setEditContent('');
      toast.success('Message modifié');
    } catch (e) {
      toast.error('Erreur modification');
    }
  };

  // Truncate (chat général)
  const handleTruncate = async (msg) => {
    const confirmed = await confirmModal('Supprimer ce message et tous les suivants ?', { confirmText: 'Supprimer', danger: true });
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('oracle_token');
      const resp = await fetch(`/api/chat/conversations/${conversationId}/messages/truncate`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_message_id: msg.id }),
      });
      const data = await resp.json();
      if (data.success) {
        setMessages(prev => prev.filter(m => m.id < msg.id));
        toast.success(`${data.deleted_count} message(s) supprimé(s)`);
      }
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  // Auto-ouvrir le panneau Voice si demandé (une seule fois)
  const voiceTriggered = useRef(false);
  useEffect(() => {
    if (autoOpenVoice && conversationId && !voiceTriggered.current) {
      voiceTriggered.current = true;
      setTimeout(() => setShowVoice(true), 300);
    }
    if (!autoOpenVoice) voiceTriggered.current = false;
  }, [autoOpenVoice, conversationId]);
  const [commMetadata, setCommMetadata] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const streamingMsgRef = useRef(null);
  // S57 — ref du conteneur de scroll pour <ScrollToBottomButton>
  const messagesContainerRef = useRef(null);
  const { sendStreamMessage, streamingContent, streamingImages, streamingDocuments, streamingDialecteMeta, currentStep, completedGroups, isStreaming, sessionMessageCount, abort: abortStream } = useStreamChat();
  const { attachments, handlePaste, addFiles, addUrl, removeAttachment, clearAttachments } = usePasteHandler({ onError: (msg) => toast.warning(msg) });

  // Charger les messages (préserve les messages aide éphémères locaux)
  useEffect(() => {
    if (!conversationId) return;
    const load = async () => {
      try {
        const token = localStorage.getItem('oracle_token');
        const res = await axios.get(`/api/chat/conversations/${conversationId}/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const dbMsgs = (res.data.messages || []).map(m => {
          const meta = typeof m.metadata === 'string' ? JSON.parse(m.metadata) : m.metadata;
          return { ...m, images: meta?.images || [] };
        });
        // Préserver les messages aide locaux (éphémères, pas en DB)
        setMessages(prev => {
          const helpMsgs = prev.filter(m => m.isHelp);
          return helpMsgs.length > 0 ? [...dbMsgs, ...helpMsgs] : dbMsgs;
        });
      } catch (err) {
        console.error('Erreur chargement messages:', err);
      }
    };
    load();
  }, [conversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const hasText = input.trim().length > 0;
    const hasAttachments = attachments.length > 0;
    if ((!hasText && !hasAttachments) || loading || isStreaming) return;

    const attachedFiles = attachments.map(a => ({
      filename: a.name, content: a.isImage ? a.base64 : a.content,
      type: a.type, size: a.size, isImage: a.isImage,
    }));
    const userMsg = hasText ? input.trim() : (hasAttachments ? 'Voici le(s) fichier(s) joint(s). Analyse le contenu.' : '');
    const displayContent = hasText
      ? input.trim() + (hasAttachments ? `\n[${attachments.length} fichier(s) joint(s)]` : '')
      : `[${attachments.length} fichier(s) joint(s)]`;

    setInput('');
    clearAttachments();
    setLoading(true);

    // Afficher immédiatement le message user
    setMessages(prev => [...prev, {
      role: 'user', content: displayContent, created_at: new Date().toISOString(),
      ...(helpMode ? { isHelp: true } : {}),
    }]);

    // Mode comm — pas de streaming (workflow spécial)
    if (conversationMode === 'comm') {
      try {
        const token = localStorage.getItem('oracle_token');
        const res = await axios.post(`/api/chat/conversations/${conversationId}/messages`, {
          message: userMsg, enable_cm_model: cmActive, forced_model: forcedModel || null,
          ...(attachedFiles.length > 0 ? { attached_files: attachedFiles } : {}),
        }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });

        setMessages(prev => [...prev, {
          role: 'assistant', content: res.data.response, model_used: res.data.model_used,
          metadata: res.data.metadata, created_at: new Date().toISOString(),
        }]);
        if (res.data.metadata?.comm_phase) setCommMetadata(res.data.metadata);
      } catch (err) {
        setMessages(prev => [...prev, {
          role: 'assistant', content: 'Erreur: ' + (err.response?.data?.error || err.message), created_at: new Date().toISOString(),
        }]);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Mode général — streaming SSE
    const streamingMsgId = Date.now();
    const isHelpRequest = helpMode;
    setMessages(prev => [...prev, {
      role: 'assistant', content: '', created_at: new Date().toISOString(),
      streaming: true, _id: streamingMsgId, isHelp: isHelpRequest,
    }]);

    let streamResult = null;
    try {
      streamResult = await sendStreamMessage(`/api/chat/conversations/${conversationId}/messages/stream`, {
        message: userMsg,
        enable_cm_model: cmActive,
        forced_model: forcedModel || null,
        ...(isHelpRequest ? { help_mode: true } : {}),
        ...(attachedFiles.length > 0 ? { attached_files: attachedFiles } : {}),
      });
    } catch (err) {
      const msg = err?.message || 'Erreur inconnue';
      const isTimeout = /ne répond plus|timeout|idle_timeout|hard_timeout/i.test(msg);
      if (isTimeout) {
        toast.error('Le modèle ne répond plus, essayez un autre modèle');
      } else {
        toast.error(msg);
      }
      setMessages(prev => prev.map(m =>
        m._id === streamingMsgId ? { ...m, content: 'Erreur: ' + msg, streaming: false } : m
      ));
    } finally {
      setLoading(false);
      if (isHelpRequest) {
        // Mode aide : finaliser le message avec le contenu accumulé, JAMAIS recharger la DB
        const helpContent = streamResult?.content || '';
        const helpModel = streamResult?.meta?.model_used || 'auto';
        setMessages(prev => prev.map(m =>
          m._id === streamingMsgId ? { ...m, content: helpContent, streaming: false, isHelp: true, model_used: helpModel } : m
        ));
      } else {
        // Mode normal : figer d'abord l'état streaming avec streamResult pour
        // éviter le flash vide entre la fin du stream (streamingImages reset à [])
        // et l'arrivée du refetch DB. Puis recharger depuis la DB pour réconcilier
        // (id canonique, timestamps, metadata complète).
        if (streamResult) {
          setMessages(prev => prev.map(m =>
            m._id === streamingMsgId ? {
              ...m,
              content: streamResult.content || m.content,
              images: streamResult.images || [],
              documents: streamResult.documents || [],
              dialecte_meta: streamResult.dialecteMeta || null,
              model_used: streamResult.meta?.model_used || m.model_used,
              streaming: false,
            } : m
          ));
        }
        try {
          const token = localStorage.getItem('oracle_token');
          const res = await axios.get(`/api/chat/conversations/${conversationId}/messages?limit=50`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.data?.messages) {
            const msgs = res.data.messages.map(m => {
              const meta = typeof m.metadata === 'string' ? JSON.parse(m.metadata) : m.metadata;
              return { ...m, images: meta?.images || [] };
            });
            setMessages(msgs);
          }
        } catch (e) {}
      }
    }
  };

  const handleClear = async () => {
    if (!(await confirmModal('Vider cette conversation ? Les messages seront supprimés.'))) return;
    try {
      const token = localStorage.getItem('oracle_token');
      await axios.delete(`/api/chat/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages([]);
      setCommMetadata(null);
    } catch (err) {
      console.error('Erreur vidage:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Comm Mode UI — Header + Welcome */}
      {conversationMode === 'comm' && (
        <div style={{ padding: '8px 24px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <CommModeBadge phase={commMetadata?.comm_phase || 'profiling'} />
          {commMetadata?.target_summary && (
            <CommTargetSummary target={commMetadata.target_summary} />
          )}
          {commMetadata?.comm_phase === 'recommending' && commMetadata?.format_options?.length > 0 && (
            <CommFormatSelector
              formats={commMetadata.format_options}
              onSelect={(formatId) => setInput(`Je choisis le format : ${formatId}`)}
            />
          )}
          {/* Welcome message si pas encore de messages user */}
          {messages.filter(m => m.role === 'user').length === 0 && (
            <div style={{
              background: 'var(--card-bg)', border: '1px solid var(--card-border)',
              borderRadius: 12, padding: '20px 24px', margin: '8px 0',
              borderLeft: '3px solid #10b981',
            }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                📡 Module Communication
              </div>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Je vais vous aider à créer un document de communication sur ITRIIA parfaitement adapté à votre cible.
              </div>
              <div style={{ fontSize: 13, color: 'var(--text)', marginTop: 12, lineHeight: 1.6 }}>
                <strong>Décrivez votre interlocuteur</strong> — son nom, sa fonction, son domaine, et ce que vous voulez obtenir de cette communication.
              </div>
              <div style={{
                display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap',
              }}>
                {[
                  'Écrire à un maire',
                  'Pitcher à un investisseur',
                  'Contacter un scientifique',
                  'Post LinkedIn',
                ].map((ex, i) => (
                  <button key={i} onClick={() => setInput(ex)} style={{
                    padding: '6px 12px', borderRadius: 8, fontSize: 12,
                    background: 'var(--input-bg)', border: '1px solid var(--card-border)',
                    color: 'var(--text)', cursor: 'pointer', transition: 'all 0.15s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.color = '#10b981'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.color = 'var(--text)'; }}
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recherche */}
      <ChatSearch
        messages={messages}
        isOpen={showChatSearch}
        onClose={() => { setShowChatSearch(false); setSearchHighlightId(null); }}
        onHighlight={setSearchHighlightId}
      />
      {/* Bouton recherche */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 24px 0' }}>
        <button onClick={() => setShowChatSearch(prev => !prev)} title="Rechercher"
          style={{ background: showChatSearch ? 'rgba(29,78,216,0.15)' : 'none', border: 'none', borderRadius: '6px', padding: '4px', cursor: 'pointer', color: showChatSearch ? '#3b82f6' : 'var(--text-secondary)' }}>
          <Search size={13} />
        </button>
      </div>
      {/* Messages */}
      <div
        ref={messagesContainerRef}
        style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '20px 24px', position: 'relative' }}
      >
        {messages.map((msg, i) => (
          <div key={i} id={`msg-${msg.id}`} ref={msg.streaming ? streamingMsgRef : null} style={{
            marginBottom: 16, display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
            ...(searchHighlightId === msg.id ? { outline: '2px solid #3b82f6', outlineOffset: '2px', borderRadius: '12px' } : {}),
          }}>
            <div className="chat-message-bubble" style={{
              maxWidth: '80%', minWidth: 0, padding: '12px 16px', borderRadius: 12,
              background: msg.role === 'user' ? 'var(--accent-light)' : 'var(--card-bg)',
              border: '1px solid var(--card-border)',
              fontSize: 14, lineHeight: 1.6, color: 'var(--text)',
              overflow: 'hidden',
              ...(msg.role === 'user' ? { whiteSpace: 'pre-wrap' } : {}),
              ...(msg.isHelp ? { borderLeft: '3px solid #10b981' } : {}),
            }}>
              {msg.role === 'user'
                ? editingMsgId === msg.id ? (
                    <div>
                      <textarea value={editContent} onChange={e => setEditContent(e.target.value)} autoFocus
                        style={{ width: '100%', minHeight: '60px', padding: '8px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: '13px', resize: 'vertical', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
                        onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleEditMessage(msg.id); if (e.key === 'Escape') { setEditingMsgId(null); setEditContent(''); } }}
                      />
                      <div style={{ display: 'flex', gap: '6px', marginTop: '6px', justifyContent: 'flex-end' }}>
                        <button onClick={() => { setEditingMsgId(null); setEditContent(''); }} style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text-secondary)', fontSize: '11px', cursor: 'pointer' }}>Annuler</button>
                        <button onClick={() => handleEditMessage(msg.id)} style={{ padding: '4px 10px', borderRadius: '4px', border: 'none', background: '#1D4ED8', color: 'white', fontSize: '11px', cursor: 'pointer' }}>Sauver</button>
                      </div>
                    </div>
                  )
                  : msg.content
                : msg.streaming
                  ? <>
                      <StreamStatus currentStep={currentStep} isStreaming={isStreaming} completedGroups={completedGroups} />
                      {streamingContent && <MarkdownMessage content={streamingContent} images={streamingImages} dialecteMeta={streamingDialecteMeta} onPreview={onPreview} />}
                    </>
                  : <MarkdownMessage content={msg.content} images={msg.images || []} dialecteMeta={msg.dialecte_meta || null} onPreview={onPreview} />
              }
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, padding: '0 4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                {msg.role === 'user'
                  ? `${user?.username || 'Vous'} • ${new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                  : <>
                      <span style={{ color: msg.isHelp ? '#10b981' : 'inherit' }}>●</span>
                      {msg.isHelp ? ' Guide ITRIIA' : ' ITRIIA Route'} • {msg.model_used || 'auto'} • {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      {msg.isHelp && <span style={{ marginLeft: 4, padding: '1px 5px', borderRadius: 3, background: 'rgba(16,185,129,0.12)', color: '#10b981', fontSize: 9, fontWeight: 600 }}>Guide</span>}
                    </>
                }
              </span>
              {!msg.streaming && (
                <div className="msg-actions" style={{ display: 'flex', gap: '2px', opacity: 0, transition: 'opacity 0.15s' }}>
                  {msg.role === 'user' && (
                    <button onClick={() => { setEditingMsgId(msg.id); setEditContent(msg.content); }} title="Modifier"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '2px' }}>
                      <Pencil size={11} />
                    </button>
                  )}
                  {msg.role === 'assistant' && msg.content && (
                    <button
                      onClick={() => setMailingModal({
                        subject: extractSubjectFromMarkdown(msg.content),
                        bodyMd: msg.content,
                      })}
                      title="Diffuser cette réponse comme newsletter"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ec4899', padding: '2px' }}>
                      <Mail size={11} />
                    </button>
                  )}
                  <button onClick={() => handleTruncate(msg)} title="Supprimer d'ici"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }}>
                    <Scissors size={11} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', gap: 6, padding: '12px 0' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--card-border)', animation: 'pulse 1s infinite' }} />
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--card-border)', animation: 'pulse 1s infinite 0.2s' }} />
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--card-border)', animation: 'pulse 1s infinite 0.4s' }} />
            <style>{`@keyframes pulse { 0%,100% { opacity:0.3; } 50% { opacity:1; } }`}</style>
          </div>
        )}
        <div ref={messagesEndRef} />
        {/* S57 — Bouton ⬇️ scroll-to-bottom factorisé */}
        <ScrollToBottomButton containerRef={messagesContainerRef} />
      </div>

      {/* Comm Document Preview */}
      {conversationMode === 'comm' && commMetadata?.document_content && (
        <div style={{ padding: '0 24px' }}>
          <CommDocumentPreview
            content={commMetadata.document_content}
            onIterate={() => setInput('Peux-tu affiner ce document ? ')}
          />
        </div>
      )}

      {/* Input area */}
      <div style={{ borderTop: '1px solid var(--topbar-border)', padding: '12px 24px 16px' }}>
        <InputToolbar
          forcedModel={forcedModel}
          onForcedModelChange={onForcedModelChange}
          onClear={handleClear}
          onManageTemplates={() => { setEditTemplateData(null); setShowTemplateManager(true); }}
          onEditTemplate={(t) => { setEditTemplateData(t); setShowTemplateManager(true); }}
          onInsertTemplate={(text) => setInput(prev => prev ? prev + '\n\n' + text : text)}
          helpMode={helpMode}
          onToggleHelp={() => setHelpMode(h => !h)}
          isStreaming={isStreaming}
          templatesVersion={templatesVersion}
        />
        {helpMode && (
          <div style={{
            padding: '6px 14px', marginBottom: '6px', borderRadius: '10px',
            display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '500',
            background: 'rgba(16,185,129,0.08)',
            border: '1px solid rgba(16,185,129,0.25)',
            color: '#10b981',
          }}>
            <span style={{ fontSize: '14px' }}>📚</span>
            Mode Aide actif — Réponses basées sur le Guide ITRIIA.
            <button onClick={() => setHelpMode(false)} style={{
              marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
              color: '#10b981', fontSize: '16px', lineHeight: 1, padding: '0 2px',
            }}>×</button>
          </div>
        )}
        <ConversationLengthIndicator
          messageCount={sessionMessageCount || messages.length}
          type="general"
        />
        <div style={{ border: '1px solid var(--input-border)', borderRadius: 12, padding: '10px 14px', background: 'var(--input-bg)' }}>
          <PasteAttachments
            attachments={attachments}
            onRemove={removeAttachment}
            showDestinationToggle={false}
          />
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <PlusMenu
              onFilesSelected={addFiles}
              onPasteContent={() => {}}
              onScreenCapture={() => toast.info('Utilisez Ctrl+V pour coller une capture d\'écran')}
              onTriggerN8N={() => {}}
              onInsertTemplate={() => {}}
              onAnalyzeUrl={addUrl}
              isProjectContext={false}
              disabled={loading}
            />
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Ecrivez votre message..."
              disabled={loading}
              style={{
                flex: 1, border: 'none', outline: 'none', resize: 'none',
                fontSize: 14, lineHeight: 1.5, color: 'var(--text)', background: 'transparent',
                minHeight: 40, maxHeight: 160,
              }}
              rows={1}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 8, gap: 8, position: 'relative' }}>
            <VoiceButton isOpen={showVoice} onClick={() => setShowVoice(!showVoice)} />
            <button
              onClick={isStreaming ? () => { abortStream(); setLoading(false); } : sendMessage}
              disabled={!isStreaming && (loading || (!input.trim() && attachments.length === 0))}
              title={isStreaming ? 'Arrêter la génération' : 'Envoyer'}
              style={{
                width: 36, height: 36, borderRadius: '50%', border: 'none',
                cursor: isStreaming ? 'pointer'
                  : (loading || (!input.trim() && attachments.length === 0)) ? 'not-allowed' : 'pointer',
                background: isStreaming
                  ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                  : (loading || (!input.trim() && attachments.length === 0)) ? 'var(--card-border)' : 'var(--accent)',
                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
                boxShadow: isStreaming ? '0 2px 12px rgba(220,38,38,0.4)' : 'none',
              }}>
              {isStreaming ? (
                <Square size={14} fill="white" />
              ) : loading ? (
                <div style={{ width: 14, height: 14, border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              ) : (
                <Send size={15} />
              )}
            </button>
            {showVoice && (
              <VoiceWidget
                onClose={() => setShowVoice(false)}
                onInjectToChat={(text) => { setInput(prev => prev ? prev + '\n\n' + text : text); setShowVoice(false); }}
              />
            )}
          </div>
        </div>
      </div>
      {/* Modal gestion templates */}
      <TemplateManager
        isOpen={showTemplateManager}
        onClose={() => { setShowTemplateManager(false); setEditTemplateData(null); }}
        onTemplatesChanged={() => setTemplatesVersion(v => v + 1)}
        initialEdit={editTemplateData}
      />

      {/* Session J+1 — Composer mailing pré-rempli depuis un message IA */}
      {mailingModal && (
        <MailingComposerModal
          initial={{ subject: mailingModal.subject, bodyMd: mailingModal.bodyMd }}
          onClose={() => setMailingModal(null)}
          onSent={() => {
            setMailingModal(null);
            toast.success('Campagne créée — voir Mailing pour le suivi.');
          }}
        />
      )}
    </div>
  );
};

export default ConversationChat;
